import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCpPortal } from '../../hooks/useCpPortal';
import { CheckCircle2, Circle, Loader2, ArrowRight } from 'lucide-react';

interface OnboardingStep {
  id: string;
  label: string;
  status: 'complete' | 'current' | 'pending';
}

// Maps an onboarding step id to the portal route that advances it.
// Keys MUST match the backend step ids in onboardingTrackerService.js STEPS
// (agreements, kyb_form, kyb_documents, compliance_review, activation,
// uat_credentials, uat_signoff, prod_float, prod_credentials).
// Steps without a route remain display-only (no misleading arrow).
const STEP_ROUTES: Record<string, string> = {
  agreements: '/client/agreements',
  kyb_form: '/client/onboarding',
  kyb_documents: '/client/onboarding',
};

export const OnboardingTracker: React.FC = () => {
  const { apiBase, authHeaders, isMirror } = useCpPortal();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<OnboardingStep[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/onboarding/status`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.data?.onboardingSteps)) {
          setSteps(data.data.onboardingSteps);
        }
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, authHeaders]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--card)] p-3 text-xs text-[var(--muted-foreground)]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading onboarding progress…
      </div>
    );
  }
  if (!steps || steps.length === 0) return null;

  const currentIdx = steps.findIndex((s) => s.status === 'current');
  const allDone = steps.every((s) => s.status === 'complete');

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--foreground)]">Onboarding progress</h3>
        <span className="text-xs text-[var(--muted-foreground)]">
          {allDone ? 'Complete' : `Step ${Math.max(currentIdx, 0) + 1} of ${steps.length}`}
        </span>
      </div>
      <ol className="space-y-2">
        {steps.map((step) => {
          const Icon = step.status === 'complete' ? CheckCircle2 : Circle;
          const iconCls =
            step.status === 'complete' ? 'text-emerald-600'
            : step.status === 'current' ? 'text-[var(--primary)]'
            : 'text-[var(--muted-foreground)]';
          const rowCls =
            step.status === 'current' ? 'bg-[var(--primary)]/5 border-[var(--primary)]/30'
            : 'border-transparent';
          const route = STEP_ROUTES[step.id];
          const clickable = !!route && !isMirror && step.status !== 'complete';
          const handle = clickable
            ? () => navigate(route)
            : undefined;
          return (
            <li
              key={step.id}
              onClick={handle}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(route!);
                }
              } : undefined}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${rowCls} ${
                clickable ? 'cursor-pointer transition-colors hover:bg-[var(--primary)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--ring)]' : ''
              }`}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${iconCls} ${step.status === 'current' ? 'animate-pulse' : ''}`} aria-hidden />
              <span className={`text-sm ${step.status === 'pending' ? 'text-[var(--muted-foreground)]' : 'text-[var(--foreground)]'}`}>
                {step.label}
              </span>
              {clickable && step.status === 'current' && (
                <ArrowRight className="ml-auto h-3.5 w-3.5 text-[var(--primary)]" aria-hidden />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
